import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import REACT_APP_API_URL from "../Api/ApiEnvironment";
import { AppState } from '@auth0/auth0-react';

interface ConfigData {
  flags: any;
}

interface CreditsData {
  balance: number;
}

interface ResellerConfigData {
  dossierName: string;
  allowedDraftTemplates: number[];
  allowedReviewTypes: string[];
  readonly: boolean;
  creditPurchaseUrl: string;
  returnUrl: string;
  language: string;
  credits: CreditsData;
  access: boolean;
  legalFlyId: number | null;
}

interface ConfigState {
  config: ConfigData;
  reseller: ResellerConfigData | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: ConfigState = {
  config: {
    flags: {},
  },
  reseller: null,
  status: 'idle',
};

export const fetchConfig = createAsyncThunk<ConfigData, string>(
  'config/fetchConfig',
  async (token) => {
    const response = await axios.get(`${REACT_APP_API_URL}/config`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }
);

export const fetchResellerConfig = createAsyncThunk<
  ResellerConfigData,
  { token: string; type: 'draft' | 'review'; resellerDocumentId: string }
>('config/fetchResellerConfig', async ({ token, type, resellerDocumentId }) => {
  return await axios
    .post(`${REACT_APP_API_URL}/config/reseller`, {
      resellerDocumentId,
      type,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((response) => {
      return response.data;
    })
    .catch(() => {
      return null;
    });
});

export const selectConfig = (state: AppState) => state.config.config;
export const selectResellerConfig = (state: AppState): ResellerConfigData =>
  state.config.reseller;

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    updateLegalFlyId: (state, action: PayloadAction<number | null>) => {
      if (state.reseller) {
        state.reseller.legalFlyId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfig.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchConfig.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.config = action.payload;
      })
      .addCase(fetchConfig.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(fetchResellerConfig.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchResellerConfig.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.reseller = action.payload;
      })
      .addCase(fetchResellerConfig.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const { updateLegalFlyId } = configSlice.actions;

export default configSlice.reducer;
